import { useCurrencyRates } from '@/hooks/useCurrencyRates';
import { usePreferredCurrency } from '@/hooks/usePreferredCurrency';
import { supabase } from '@/integrations/supabase/client';
import { getConvertedPrice, getCurrencySymbol } from '@/lib/currencies';
import { X } from 'lucide-react';
import { posthog } from 'posthog-js';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const SHOWN_KEY = 'bookstera_visitor_offer_shown';
const DISMISSED_KEY = 'bookstera_visitor_offer_dismissed';
const PRICE_USD = 27;
const EXCLUDED_PATHS = ['/signin', '/signup', '/reset', '/lt3/checkout', '/lt2/checkout', '/lifetime-funnel/checkout'];
const isExcludedPath = (path: string) =>
  path === '/lt' || EXCLUDED_PATHS.some((excluded) => path === excluded || path.startsWith(`${excluded}/`));

export function VisitorLifetimeOffer() {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedCurrency } = usePreferredCurrency();
  const { currencyRates } = useCurrencyRates();
  const [visible, setVisible] = useState(false);
  const [collapsed, setCollapsed] = useState(() =>
    typeof window !== 'undefined' && localStorage.getItem(DISMISSED_KEY) === '1'
  );
  const [priceReady, setPriceReady] = useState(false);

  useEffect(() => {
    let active = true;
    let timer: number | undefined;
    const scheduleOffer = async () => {
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        setCollapsed(false);
        return;
      }
      if (!active || localStorage.getItem(DISMISSED_KEY) || localStorage.getItem(SHOWN_KEY)) return;
      const path = window.location.pathname;
      if (isExcludedPath(path)) return;
      timer = window.setTimeout(() => {
        if (!active) return;
        localStorage.setItem(SHOWN_KEY, '1');
        setCollapsed(false);
        setVisible(true);
      }, 6000);
    };
    void scheduleOffer();
    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        if (timer !== undefined) window.clearTimeout(timer);
        setVisible(false);
        setCollapsed(false);
      }
    });
    return () => {
      active = false;
      if (timer !== undefined) window.clearTimeout(timer);
      authListener.subscription.unsubscribe();
    };
  }, [location.pathname]);

  const currency = selectedCurrency.toUpperCase();
  const localizedPrice = currencyRates?.[currency]
    ? `${getCurrencySymbol(currency, currencyRates)}${getConvertedPrice(PRICE_USD, currency, currencyRates)}`
    : new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(PRICE_USD);

  useEffect(() => {
    if (currency === 'USD' || currencyRates?.[currency]) setPriceReady(true);
  }, [currency, currencyRates]);

  useEffect(() => {
    if (!visible) return;
    posthog.capture('homepage_lifetime_offer_shown', {
      source: 'homepage_visitor_modal',
      page_path: location.pathname,
      currency,
      displayed_price: localizedPrice,
      price_usd: PRICE_USD,
    });
  }, [visible, location.pathname, currency, localizedPrice]);

  if ((!visible && !collapsed) || !priceReady || isExcludedPath(location.pathname)) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, '1');
    posthog.capture('homepage_lifetime_offer_dismissed', {
      source: 'homepage_visitor_modal',
      page_path: window.location.pathname,
      currency,
      displayed_price: localizedPrice,
      price_usd: PRICE_USD,
    });
    setVisible(false);
    setCollapsed(true);
  };

  const reopenOffer = () => {
    setCollapsed(false);
    setVisible(true);
  };

  const claimOffer = () => {
    posthog.capture('homepage_lifetime_offer_clicked', {
      source: 'homepage_visitor_modal',
      page_path: window.location.pathname,
      destination: '/lt',
      currency,
      displayed_price: localizedPrice,
      price_usd: PRICE_USD,
    });
    setVisible(false);
    setCollapsed(true);
    localStorage.setItem(DISMISSED_KEY, '1');
    navigate('/lt');
  };

  return (
    <aside className={`fixed bottom-4 right-4 z-[60] overflow-hidden border border-[#e6e8df] bg-white shadow-[0_18px_60px_rgba(24,52,45,.24)] transition-[width,height,border-radius,box-shadow,transform] duration-500 ease-[cubic-bezier(.22,1,.36,1)] ${collapsed ? 'h-14 w-[min(15.5rem,calc(100vw-2rem))] rounded-full' : 'h-auto w-[min(22rem,calc(100vw-2rem))] rounded-2xl animate-in slide-in-from-bottom-4'}`} aria-label="Bookstera limited time offer">
      <div className={`grid transition-[grid-template-rows,opacity,transform] duration-500 ease-[cubic-bezier(.22,1,.36,1)] ${collapsed ? 'pointer-events-none grid-rows-[0fr] scale-95 opacity-0' : 'grid-rows-[1fr] scale-100 opacity-100'}`} aria-hidden={collapsed}>
        <div className="min-h-0 overflow-hidden">
      <div className="relative bg-[#173b33] px-5 py-3 text-center text-sm font-extrabold uppercase tracking-[.12em] text-[#f2d6a7]">
        A Bookstera welcome offer
        <span className="absolute -bottom-2 left-1/2 h-4 w-4 -translate-x-1/2 rotate-45 bg-[#173b33]" />
      </div>
      <button type="button" onClick={dismiss} className="absolute right-2 top-2 z-10 grid h-7 w-7 place-items-center rounded-full bg-white/90 text-slate-500 shadow-sm hover:bg-white hover:text-slate-900" aria-label="Close offer">
        <X className="h-4 w-4" />
      </button>
      <div className="px-5 pb-5 pt-6 text-center">
        <h2 className="font-kameron text-[1.65rem] font-bold leading-tight text-[#173b33]">Your next book starts here.</h2>
        <p className="mt-2 text-sm leading-5 text-slate-600">Get lifetime access to Bookstera’s starter package for one payment.</p>
        <p className="mt-4 text-sm font-semibold text-slate-700">One-time price</p>
        <p className="mt-0.5 text-3xl font-black tracking-tight text-[#173b33]">{localizedPrice}</p>
        <p className="mt-1 text-xs text-slate-500">No monthly payments</p>
        <button type="button" onClick={claimOffer} className="mt-4 min-h-12 w-full rounded-xl bg-[#173b33] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#245447] focus:outline-none focus:ring-2 focus:ring-[#173b33] focus:ring-offset-2">
          Claim the offer
        </button>
        <p className="mt-2 text-[11px] text-slate-500">Secure checkout · Lifetime access</p>
      </div>
        </div>
      </div>
      <button type="button" onClick={reopenOffer} aria-label={`Reopen lifetime offer, ${localizedPrice} one-time`} className={`absolute inset-0 flex items-center justify-center gap-2 whitespace-nowrap px-4 text-sm font-bold text-[#173b33] transition-[opacity,transform] duration-300 ${collapsed ? 'translate-y-0 opacity-100 delay-200' : 'pointer-events-none translate-y-2 opacity-0'}`} tabIndex={collapsed ? 0 : -1}>
        <span className="grid h-7 w-7 place-items-center rounded-full bg-[#edf2e9] text-[#173b33]" aria-hidden="true">✦</span>
        <span>Lifetime access</span>
        <span className="rounded-full bg-[#f2d6a7] px-2 py-1 text-xs">{localizedPrice}</span>
      </button>
    </aside>
  );
}
